import { useCallback, useRef, useState } from "react";

interface QueueItem {
  id: string;
  callback: () => Promise<void>;
}

export interface UseCallbackQueueProps {
  onQueueStart?: () => Promise<unknown> | void;
  onQueueFinished?: () => Promise<unknown> | void;
  onCallbackFinished?: (
    id: string,
    queue: QueueItem[]
  ) => Promise<unknown> | void;
  deduplicationStrategy?: "keep-last";
}

/**
 * Processes a queue of callbacks in order and sequentially without parallel execution.
 * Deduplication of queued callbacks can be configured.
 */
export default function useCallbackQueue({
  onQueueStart,
  onQueueFinished,
  onCallbackFinished,
  deduplicationStrategy,
}: UseCallbackQueueProps = {}) {
  const queue = useRef<QueueItem[]>([]);
  const currentlyProcessingId = useRef<string | undefined>(undefined);
  const [idProcessing, setIdProcessing] = useState<string | undefined>(
    undefined
  );
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const setProcessingId = useCallback((id: string | undefined) => {
    currentlyProcessingId.current = id;
    setIdProcessing(id);
  }, []);

  const processQueue = useCallback(async () => {
    if (currentlyProcessingId.current || queue.current.length === 0) return;

    await onQueueStart?.();
    setIsProcessing(true);

    while (queue.current.length > 0) {
      const { id, callback } = queue.current.shift();

      setProcessingId(id);
      if (callback) {
        try {
          await callback();
        } catch (err) {
          console.error(`Error processing callback for id ${id}:`, err);
        }
      }

      // Example: A, B, C, C, C, B, A.
      // Result: C, B, A.
      if (deduplicationStrategy === "keep-last") {
        const seen = new Set<string>();
        queue.current = queue.current
          .reverse()
          .filter((item) => {
            if (seen.has(item.id)) {
              return false;
            }
            seen.add(item.id);
            return true;
          })
          .reverse();
      }

      await onCallbackFinished?.(id, queue.current);
    }

    await onQueueFinished?.();
    setIsProcessing(false);
    setProcessingId(undefined);
  }, [
    onQueueStart,
    onQueueFinished,
    deduplicationStrategy,
    onCallbackFinished,
    setProcessingId,
  ]);

  const addToQueue = useCallback(
    (id: string, callback: () => Promise<void>) => {
      queue.current.push({ id, callback });
      void processQueue();
    },
    [processQueue]
  );

  return {
    addToQueue,
    processingId: idProcessing,
    isProcessing,
  };
}
